import { computed, ref, watch, type ComputedRef } from "vue";
import { storeToRefs } from "pinia";
import {
  fromMaybe,
  isJust,
  isNothing,
  just,
  mapMaybe,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { joinProjectPath } from "../lib/runtime";
import { readProjectFile } from "../lib/tauri/core";
import {
  defaultZwSceneFormatting,
  defaultZwStatusBar,
  scenePath,
  type ZwSceneFormatting,
} from "../lib/zwConfig";
import { scenePeekHead, scenePeekTail } from "../lib/sceneNeighborPeek";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useDisplayModeStore } from "../stores/useDisplayModeStore";

export type SceneNeighborPeekEdge = {
  path: string;
  /** Mini `.zwdoc` for ProseMirror (same formatting as the open scene). */
  text: string;
  blockCount: number;
};

/**
 * Loads prev/next scene peeks for the open manuscript scene when the project
 * setting is on and focus mode is off (#264).
 */
export function useSceneNeighborPeeks(): {
  enabled: ComputedRef<boolean>;
  sceneFormatting: ComputedRef<ZwSceneFormatting>;
  prevPeek: ComputedRef<Maybe<SceneNeighborPeekEdge>>;
  nextPeek: ComputedRef<Maybe<SceneNeighborPeekEdge>>;
} {
  const session = useEditorSessionStore();
  const display = useDisplayModeStore();
  const { activeSceneInfo, rootPath, projectConfig, activeBasename } =
    storeToRefs(session);

  const sceneFormatting = computed(() =>
    matchMaybe(projectConfig.value, {
      just: (config) => config.sceneFormatting,
      nothing: () => defaultZwSceneFormatting(),
    }),
  );

  const showNeighborScenePeeks = computed(() =>
    matchMaybe(projectConfig.value, {
      just: (config) => config.statusBar.showNeighborScenePeeks,
      nothing: () => defaultZwStatusBar().showNeighborScenePeeks,
    }),
  );

  const enabled = computed(
    () =>
      showNeighborScenePeeks.value === true &&
      display.isFocus === false &&
      isJust(activeSceneInfo.value),
  );

  const neighbors = computed(
    (): {
      prevPath: Maybe<string>;
      nextPath: Maybe<string>;
    } => {
      if (enabled.value === false) {
        return { prevPath: nothing, nextPath: nothing };
      }
      return matchMaybe(activeSceneInfo.value, {
        nothing: () => ({ prevPath: nothing, nextPath: nothing }),
        just: (active) => {
          const scenes = active.chapter.scenes;
          const index = scenes.findIndex((s) => s.file === active.sceneFile);
          if (index < 0) return { prevPath: nothing, nextPath: nothing };
          const prev = toMaybe(scenes[index - 1]);
          const next = toMaybe(scenes[index + 1]);
          return {
            prevPath: mapMaybe(prev, (scene) =>
              scenePath(active.folder, scene.file),
            ),
            nextPath: mapMaybe(next, (scene) =>
              scenePath(active.folder, scene.file),
            ),
          };
        },
      });
    },
  );

  const cached = ref<Record<string, string>>({});

  async function loadPath(path: string, root: string): Promise<void> {
    try {
      const raw = await readProjectFile(joinProjectPath(root, path));
      if (fromMaybe(session.rootPath) !== root) return;
      cached.value = { ...cached.value, [path]: raw };
    } catch {
      // Cloud / missing sibling: leave absent rather than broken.
    }
  }

  watch(
    [
      enabled,
      () => fromMaybe(rootPath.value) ?? "",
      () => fromMaybe(neighbors.value.prevPath) ?? "",
      () => fromMaybe(neighbors.value.nextPath) ?? "",
      () => fromMaybe(activeBasename.value) ?? "",
    ],
    () => {
      if (enabled.value === false || isNothing(rootPath.value)) {
        cached.value = {};
        return;
      }
      const root = rootPath.value.value;
      const paths: string[] = [];
      if (isJust(neighbors.value.prevPath)) {
        paths.push(neighbors.value.prevPath.value);
      }
      if (isJust(neighbors.value.nextPath)) {
        paths.push(neighbors.value.nextPath.value);
      }
      void Promise.all(paths.map((path) => loadPath(path, root)));
    },
    { immediate: true },
  );

  // When the writer leaves a scene, drop its cache so a later peek reloads.
  watch(
    () => fromMaybe(activeBasename.value) ?? "",
    (path, prev) => {
      if (prev !== "" && prev !== path) {
        const { [prev]: _removed, ...rest } = cached.value;
        cached.value = rest;
      }
    },
  );

  const prevPeek = computed((): Maybe<SceneNeighborPeekEdge> => {
    const pathM = neighbors.value.prevPath;
    if (isNothing(pathM)) return nothing;
    const raw = toMaybe(cached.value[pathM.value]);
    if (isNothing(raw)) return nothing;
    const slice = scenePeekTail(raw.value);
    if (isNothing(slice)) return nothing;
    return just({
      path: pathM.value,
      text: slice.value.text,
      blockCount: slice.value.blockCount,
    });
  });

  const nextPeek = computed((): Maybe<SceneNeighborPeekEdge> => {
    const pathM = neighbors.value.nextPath;
    if (isNothing(pathM)) return nothing;
    const raw = toMaybe(cached.value[pathM.value]);
    if (isNothing(raw)) return nothing;
    const slice = scenePeekHead(raw.value);
    if (isNothing(slice)) return nothing;
    return just({
      path: pathM.value,
      text: slice.value.text,
      blockCount: slice.value.blockCount,
    });
  });

  return { enabled, sceneFormatting, prevPeek, nextPeek };
}
